import { Grid, styled } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../Common/_hooks/useAppDispatch'
import { useAppSelector } from '../../Common/_hooks/useAppSelector'
import ItemList from '../../Common/ItemList/ItemList'
import { SaveUserPrefs } from '../../UserData/_actions/UserDataActions'
import type { ProductSortType } from '../../UserData/_stores/UserPrefsStore'
import type { UserProductDesignStore } from '../../UserData/_stores/UserProductDesignStore'
import AccountNoResults from '../../Account/AccountNoResults'
import AccountFilterDrawer from '../../Account/Filters/AccountFilterDrawer'
import AccountFilterSection from '../../Account/Filters/AccountFilterSection'
import AccountLayoutFilter from '../../Account/Filters/AccountLayoutFilter'
import AccountLimitFilter from '../../Account/Filters/AccountLimitFilter'
import AccountSortFilter, { type AccountSortOption } from '../../Account/Filters/AccountSortFilter'
import ProductSearchBar, { type ProductSearchParams } from '../../Account/Products/ProductSearchBar'
import ProductSkeletonList from '../../Account/Products/ProductSkeletonList'
import Dialog from '../Dialog/Dialog'
import DialogPaper from '../Dialog/DialogPaper'
import DialogContent from '../Dialog/DialogContent'
import DialogTitle from '../Dialog/DialogTitle'
import ProductDesign from '../../Account/Products/ProductDesign'

const StyledDialogPaper = styled(DialogPaper)(({ theme }) => ({
    height: '90%',
    
    '&.MuiDialog-paperFullScreen': {
        height: '100%',
    },
    
    [theme.breakpoints.down('dialog_mobile')]: {
        borderRadius: 0,
        height: '100%',
        margin: 0,
        maxHeight: 'none',
        width: '100%',
    }
}))

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    boxSizing: 'border-box',
    minHeight: 0,
    padding: theme.spacing(3, 3, 0, 3),
    marginTop: 0,

    [theme.breakpoints.up('md')]: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    
    [theme.breakpoints.down('md')]: {
        overflowX: 'hidden',
        overflowY: 'auto',
        padding: theme.spacing(2),
    }
}))

const ProductResultsScroller = styled('div')(({ theme }) => ({
    paddingBottom: theme.spacing(2),

    [theme.breakpoints.up('md')]: {
        flex: '1 1 auto',
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        scrollbarGutter: 'stable',
        WebkitOverflowScrolling: 'touch',
    },
}))

const SEARCH_FIELDS = ['search', 'categories', 'collections', 'sort']

export interface Props {
    opened: boolean
    category?: number
    onClose?: () => void
    onSelect?: (design: UserProductDesignStore) => void
}

export default function DesignBrowser(props: Props) {
    const [t] = useTranslation('account-v2')
    const dispatch = useAppDispatch()
    
    const userLayout = useAppSelector(state => state.get('userData').get('prefs').get('accountProductsLayout'))
    const userLimit = useAppSelector(state => state.get('userData').get('prefs').get('accountPageLimit'))
    const userSort = useAppSelector(state => state.get('userData').get('prefs').get('accountProductsSort'))
    const itemList = useAppSelector(state => state.get('userData').get('productDesigns').get('data'))
    
    const defaultSearch = useMemo<ProductSearchParams>(() => ({
        categories: props.category !== undefined ? [String(props.category)] : [],
        search: [],
        collections: [],
        sort: [userSort],
    }), [props.category, userSort])
    
    const [search, setSearch] = useState<ProductSearchParams>(defaultSearch)
    
    const filteredSearch = useMemo(() => {
        const out = {...search}
        if (props.category !== undefined) {
            out.categories = [String(props.category)]
        }
        return out
    }, [search, props.category])
    
    const categoryId = props.category !== undefined
        ? String(props.category)
        : (search.categories.length > 0 ? search.categories[0] : undefined)
    const categoryName = useAppSelector(state => categoryId !== undefined
        ? state.get('appData').get('viewCategories').get(categoryId)?.get('name')
        : undefined)
    const title = categoryName ?? t('Products')
    
    const listId = useMemo(() => 'account-product-designs', [])
    const [limit, setLimit] = useState(userLimit)
    const [layout, setLayout] = useState(userLayout)
    
    const onSelectProductDesign = useCallback((item: UserProductDesignStore) => {
        props.onSelect?.(item);
        props.onClose?.();
    }, [itemList, props.onSelect, props.onClose])
    
    const renderItem = useCallback((item: UserProductDesignStore) => {
        return <ProductDesign
        item={item}
        view={layout}
        onClick={onSelectProductDesign}
        />
    }, [layout, onSelectProductDesign])
    
    const renderList = useCallback((items: ReactElement | null, links: ReactElement | null, loading: boolean) => {
        return <>
            <ProductResultsScroller>
                <Grid container spacing={{ xs: 2, md: 3 }}>
                    {loading
                        ? <ProductSkeletonList
                            count={userLimit}
                            layout={layout}
                            showSelection={false}
                            showSecondaryActions={false}
                        />
                        : (items ?? <Grid item xs={12}>
                            <AccountNoResults compact />
                        </Grid>)
                    }
                </Grid>
            </ProductResultsScroller>

            {links}
        </>
    }, [t, limit, layout])
    
    const onChangeSort = useCallback((nextSort: ProductSortType) => {
        setSearch(value => ({
            ...value,
            sort: [nextSort],
        }))
        
        if (nextSort === userSort) {
            return
        }
        
        const fd = new FormData()
        fd.append('accountProductsSort', nextSort)
        dispatch(SaveUserPrefs(fd, true))
    }, [dispatch, userSort])
    
    useEffect(() => {
        setSearch(value => {
            if (value.sort[0] === userSort) {
                return value
            }
            
            return {
                ...value,
                sort: [userSort],
            }
        })
    }, [userSort])
    
    const selectedSort = search.sort[0] ?? userSort
    const sortOptions: AccountSortOption[] = [
        { value: 'n-to-o', label: t('Newest first') },
        { value: 'o-to-n', label: t('Oldest first') },
        { value: 'a-to-z', label: t('A to Z') },
        { value: 'z-to-a', label: t('Z to A') },
    ]
    
    return <Dialog
        open={props.opened}
        onClose={props.onClose}
        fullWidth={true}
        maxWidth="xl"
        PaperComponent={StyledDialogPaper}
        showCloseButton
        fullScreenOnMobile="md"
        closeButtonSx={{
            backgroundColor: 'common.white',
            '&:hover': {
                backgroundColor: 'common.white',
            }
        }}
    >
        <DialogTitle>{title}</DialogTitle>
        <StyledDialogContent>
            <ProductSearchBar
                placeholder={t('Search your products...')}
                value={filteredSearch}
                onChange={setSearch}
                >
                    <AccountFilterDrawer inDialog={true}>
                        <AccountFilterSection title={t('Display')}>
                            <AccountSortFilter
                                label={t('Sort by')}
                                value={selectedSort}
                                options={sortOptions}
                                onChange={onChangeSort}
                            />
                            <AccountLimitFilter
                                label={t('Results per page')}
                                value={limit}
                                onChange={setLimit}
                            />
                            <AccountLayoutFilter
                                label={t('Layout')}
                                value={layout}
                                onChange={setLayout}
                            />
                        </AccountFilterSection>
                    </AccountFilterDrawer>
            </ProductSearchBar>
            <ItemList
                listId={listId}
                controlled={true}
                dataType={'productDesigns'}
                item={renderItem}
                searchQuery={filteredSearch}
                defaultSearchQuery={defaultSearch}
                searchFields={SEARCH_FIELDS}
                limit={limit}
                render={renderList}
            />
        </StyledDialogContent>
    </Dialog>
}
